// src/oura/newscan/fixtures/assemble.ts
//
// Turns a stored draft into what GET /scan-drafts/{id} returns: plans the 50
// from the business they were drafted from, renders each with the words and
// the staff's edits, lints the set, and computes the words, decisions, recipe
// change, diff and Search Console block. Pure apart from the `now` it is given.
//
// A brand draft (Online, v4.5) plans its 50 from the sample's pre-authored
// rows (brand.ts) instead of templates, carries each row's market and Google
// search, runs the brand lint and returns the demand pull as `draft.demand`.

import { customerQuestionOffers } from "./formCards";
import type {
    AnyScanDraftJobStep,
    ScanDraft,
    ScanDraftCounts,
    ScanDraftJobStep,
    ScanDraftQuestion,
    ScanDraftRecipeChange,
    ScanDraftSearchState,
    ScanQuestionCheck,
} from "@service/aeo/scanDraftTypes";
import type { AssembledRow, DraftState } from "./model";
import { alternativesFor, rowCtx } from "./alternatives";
import { brandDemand, marketCodes, spareById, spareId, sparesFor } from "./brand";
import { computeDecisions, type AutoRewordedRow } from "./decisions";
import { lintRows, type LintContext, type LintIssue } from "./lint";
import { FORCED_SLOT_KEY, RECIPES, templateById, templateVars, type TemplateDef } from "./recipe";
import { fillOf, placeNames, planRows, renderTemplate, type PlannedRow } from "./render";
import { rowSearchHit, rowSearchHitByText } from "./search";
import { sampleFor, type FixtureSample } from "./samples";
import { buildWords, languagesOn, wordSource, type WordSource } from "./words";
import { changedVsPitch } from "./firstCheck";


/** A baseline may move ("Fix the setup") only this many days after it was measured (call 12). */
export const BASELINE_MOVE_DAYS = 30;

export const JOB_STEPS: ScanDraftJobStep[] = ["reading_site", "checking_outlets", "researching", "writing_words", "filling", "checking"];
/** Brand drafts (SPEC-v45 §6): no outlets to check; the Google searches are pulled per market instead. */
export const BRAND_JOB_STEPS: AnyScanDraftJobStep[] = ["reading_site", "pulling_demand", "researching", "writing_words", "filling", "checking"];

/** A brand draft: Online, so no outlet a customer visits (scanDraftModeFor). */
export const isBrand = (state: Pick<DraftState, "built">): boolean => state.built.buy === "online";

/** The job steps a draft is written in, in order. */
export function jobSteps(state: Pick<DraftState, "built">): AnyScanDraftJobStep[] {
    return isBrand(state) ? BRAND_JOB_STEPS : JOB_STEPS;
}

/** Why a brand draft cannot be written in the fixtures: no pre-authored set for the site. Null when it can. */
export function brandUnavailable(state: Pick<DraftState, "built" | "sampleDomain">): string | null {
    if (!isBrand(state)) return null;
    const sample = state.sampleDomain ? sampleFor(state.sampleDomain) : null;
    return sample?.brand ? null : "No Google searches in the samples for this site. Use samplesupps.com or samplesleep.my.";
}

export interface SearchConnection {
    connected: boolean;
    property: string | null;
    connectedAt: number | null;
    /** A year of searches is being pulled (`search.pulling`). */
    pulling: boolean;
}

export interface Assembly {
    sample: FixtureSample | null;
    src: WordSource;
    rows: AssembledRow[];
    issues: LintIssue[];
    lintCtx: LintContext;
}

const NAME_STOP = new Set(["the", "and", "co", "sdn", "bhd", "clinic", "centre", "center", "studio", "store", "shop", "group", "plt"]);

/**
 * Business-name words a question must not carry: the distinctive ones, not the
 * words the recipe and the customers use anyway ("place", "clinic", "wellness").
 */
function nameTokensFor(state: DraftState, sample: FixtureSample | null, src: WordSource, plans: PlannedRow[]): string[] {
    if (sample) return sample.nameTokens;
    const common = new Set<string>();
    const addWords = (text: string) =>
        text
            .toLowerCase()
            .split(/[^a-z0-9]+/)
            .forEach((w) => common.add(w));
    plans.forEach((p) => {
        addWords(p.template.text.replace(/\{[a-z_0-9]+\}/g, " "));
        templateVars(p.template.text).forEach((v) => addWords(src.value(v)));
    });
    addWords(state.built.category);
    return state.built.businessName
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .split(/\s+/)
        .filter((w) => w.length >= 3 && !NAME_STOP.has(w) && !common.has(w));
}

function templateTextFor(state: DraftState, plan: PlannedRow, sample: FixtureSample | null): { text: string; id: string | null } {
    const ov = state.rows[plan.key];
    // Brand "Try another": another pulled search, asked as written.
    const spare = ov?.tpl?.startsWith("d:") ? spareById(sample?.brand, ov.tpl) : null;
    if (spare?.question && ov?.tpl) return { text: spare.question, id: ov.tpl };
    if (ov?.tpl) {
        const t = templateById(ov.tpl);
        if (t) return { text: t.text, id: t.id };
    }
    const old = sample?.existing?.oldTemplates[plan.key];
    if (old && state.oldKeys.includes(plan.key)) return { text: old, id: null };
    const auto = autoTemplate(state, plan);
    return { text: auto.text, id: auto.id };
}

/** The recipe's template for a row, or the one the dedupe pass moved it to. */
function autoTemplate(state: DraftState, plan: PlannedRow): TemplateDef {
    const moved = state.auto?.[plan.key]?.tpl;
    return (moved && templateById(moved)) || plan.template;
}

/**
 * The row's own word values: staff "try another" values, else the dedupe
 * pass's word fallback. A staff "try another" (either kind) replaces the pass's
 * values, so the pass's words never mix into a staff pick.
 */
export function rowValues(state: DraftState, key: string): Record<string, string> {
    const ov = state.rows[key];
    if (ov?.tpl || ov?.values) return ov.values ?? {};
    return state.auto?.[key]?.values ?? {};
}

function assembleRow(state: DraftState, plan: PlannedRow, n: number, src: WordSource, sample: FixtureSample | null): AssembledRow {
    const ov = state.rows[plan.key] ?? {};
    const shell: AssembledRow = { plan, question: {} as ScanDraftQuestion, fresh: "", proposed: "", old: null, missing: [] };
    const ctx = rowCtx(shell);
    const own = rowValues(state, plan.key);
    const word = (v: string) => own[v] ?? src.value(v);
    const autoValues = state.auto?.[plan.key]?.values ?? {};
    const current = templateTextFor(state, plan, sample);
    const live = renderTemplate(current.text, ctx, word);
    const oldTpl = sample?.existing?.oldTemplates[plan.key];
    // A brand row asked as another pulled search carries that search.
    const spare = ov.tpl?.startsWith("d:") ? spareById(sample?.brand, ov.tpl) : null;
    const brandFields = plan.brand
        ? { market: plan.market, trace: spare ? { suggestion: spare.text, market: spare.market } : plan.trace ?? null }
        : {};
    const base = {
        n,
        slot_n: n,
        group: plan.group,
        check: plan.check,
        ...brandFields,
        town: plan.town,
        language: plan.language,
        changed: false,
        alt_count: 0,
        search: null,
        // "Try another" taken on this row: another template or other words.
        swapped: Boolean(ov.tpl || (ov.values && Object.keys(ov.values).length > 0)),
    };
    let question: ScanDraftQuestion;
    let missing = live.missing;
    if (ov.text !== undefined) {
        question = {
            ...base,
            fill: fillOf(plan, live),
            text: ov.text,
            fill_vars: [],
            fill_values: {},
            locked: ov.locked ?? true,
            reworded: true,
            swapped: false,
            original_text: ov.original ?? null,
        };
        missing = [];
    } else if (ov.locked && ov.frozen) {
        question = { ...base, ...ov.frozen, locked: true, reworded: false, original_text: null };
        missing = Array.from(ov.frozen.text.matchAll(/\{([a-z_0-9]+)\}/g), (m) => m[1]);
    } else {
        question = {
            ...base,
            fill: fillOf(plan, live),
            text: live.text,
            fill_vars: live.fill_vars,
            fill_values: live.fill_values,
            locked: false,
            reworded: false,
            original_text: null,
        };
    }
    return {
        plan,
        question,
        fresh: live.text,
        proposed: renderTemplate(autoTemplate(state, plan).text, ctx, (v) => autoValues[v] ?? src.value(v)).text,
        old: oldTpl ? renderTemplate(oldTpl, ctx, (v) => src.value(v)).text : null,
        missing,
    };
}

/** The 50 as they stand, with their lint. */
export function assemble(state: DraftState): Assembly {
    const sample = state.sampleDomain ? sampleFor(state.sampleDomain) : null;
    const src = wordSource(sample, state.built, state.wordEdits, state.round);
    const brand = isBrand(state);
    const plans = planRows({
        business: state.built,
        places: sample?.places ?? {},
        languagesOn: languagesOn(state.built, sample),
        hasWord: (v) => Boolean(src.value(v)),
        brand: brand ? sample?.brand ?? null : null,
        demandRound: state.demandRound ?? 0,
        languageShare: sample?.languageShare ?? { ms: 0, zh: 0 },
    });
    const rows = plans.map((plan, i) => assembleRow(state, plan, i + 1, src, sample));
    const differentiatorUsed = rows.some((r) => r.question.fill_vars.includes("differentiator"));
    const lintCtx: LintContext = {
        nameTokens: nameTokensFor(state, sample, src, plans),
        placeNames: placeNames(state.built),
        category: src.value("category"),
        brand:
            brand && sample?.brand
                ? {
                      categoryWords: sample.brand.categoryWords,
                      claimPhrases: Array.from(new Set([state.built.businessName.trim(), ...sample.brand.claimPhrases].filter(Boolean))),
                      places: [
                          ...state.built.outlets.map((o) => o.name),
                          ...Object.keys(sample.places),
                          ...Object.values(sample.places).flatMap((f) => [f.building ?? "", f.transit ?? "", f.landmark ?? ""]),
                      ].filter(Boolean),
                      markets: marketCodes(state.built),
                      differentiator: differentiatorUsed ? src.value("differentiator") || null : null,
                      sellsToBusinesses: state.built.sellsToBusinesses === true,
                  }
                : null,
    };
    const issues = lintRows(
        rows.map((r) => ({
            n: r.question.n,
            key: r.plan.key,
            check: r.plan.check,
            text: r.question.text,
            reworded: r.question.reworded,
            ctx: r.plan.ctx,
            ...(r.plan.brand ? { group: r.plan.group, fill: r.question.fill, trace: r.question.trace ?? null } : {}),
        })),
        lintCtx,
    );
    return { sample, src, rows, issues, lintCtx };
}

/**
 * The rows the dedupe pass reworded that still carry its wording: a staff
 * reword or "try another" on one takes it off the list.
 */
function autoRewordedRows(state: DraftState, rows: AssembledRow[]): AutoRewordedRow[] {
    const out: AutoRewordedRow[] = [];
    rows.forEach((r) => {
        const auto = state.auto?.[r.plan.key];
        // A recipe change row still on its old wording is not asked with the pass's template.
        if (!auto || r.question.reworded || r.question.swapped || state.oldKeys.includes(r.plan.key)) return;
        if (r.question.text === auto.from) return;
        out.push({ n: r.question.n, from: auto.from, to: r.question.text });
    });
    return out;
}

/** "Try another" for one row of an assembly. */
export function rowAlternatives(state: DraftState, a: Assembly, row: AssembledRow) {
    const current = templateTextFor(state, row.plan, a.sample);
    const set = a.sample?.brand;
    const searches =
        set && row.plan.brand
            ? sparesFor(set, row.plan.brand).map((sp) => ({ id: spareId(sp), text: sp.question as string, suggestion: sp.text }))
            : undefined;
    return alternativesFor({
        searches,
        row,
        rows: a.rows,
        src: a.src,
        home: state.built.buy === "we_go_to_them",
        currentTemplate: current.text,
        currentTemplateId: current.id,
        rowValues: rowValues(state, row.plan.key),
    });
}

/** Business inputs that feed the 50: a change to any of them makes the draft stale. */
function feedsTheSet(b: DraftState["business"]): string {
    // Brand: the buy answer, the markets in order and who it sells to write the 50. Partner products only change the counting.
    if (b.buy === "online") {
        return JSON.stringify({
            buy: b.buy,
            markets: (b.markets ?? []).map((m) => m.code),
            sellsToBusinesses: b.sellsToBusinesses === true,
            languages: b.languages,
        });
    }
    return JSON.stringify({
        buy: b.buy,
        city: b.city,
        category: b.category,
        outlets: b.outlets.map((o) => [o.name, o.town, o.source, o.confirmed === true]),
        services: b.services.map((s) => [s.name, s.source]),
        languages: b.languages,
    });
}

export function isStale(state: DraftState): boolean {
    return feedsTheSet(state.business) !== feedsTheSet(state.built);
}

function countsOf(questions: ScanDraftQuestion[], brand: boolean): ScanDraftCounts {
    // Local keys always present (0 on a brand draft); brand keys only on a brand draft.
    const check: ScanDraftCounts["check"] = brand ? { location: 0, specialty: 0, intent: 0, pick: 0, source: 0, supplier: 0 } : { location: 0, specialty: 0, intent: 0 };
    const tally = check as Record<ScanQuestionCheck, number>;
    const group: Record<string, number> = {};
    questions.forEach((q) => {
        const c = q.check as ScanQuestionCheck;
        tally[c] = (tally[c] ?? 0) + 1;
        group[q.group] = (group[q.group] ?? 0) + 1;
    });
    return { check, group, single: !brand && check.location === 14, total: questions.length };
}

function recipeOf(state: DraftState) {
    const target = RECIPES[state.built.buy];
    if (state.kind === "recipe_change") {
        const moved = state.recipeMode === "take_all" || state.recipeMode === "choose_each";
        const now = moved ? target : state.recipeFrom;
        return { stamp: now.stamp, proposedStamp: target.stamp, shape: state.built.buy, label: now.label, proposedLabel: target.label };
    }
    const now = state.kind === "edit_set" ? state.recipeFrom : target;
    return { stamp: now.stamp, proposedStamp: null, shape: state.built.buy, label: now.label, proposedLabel: null };
}

function iso(ms: number | null): string | null {
    return ms === null ? null : new Date(ms).toISOString();
}

/** The job's step at `now`, and whether it has finished. */
export function jobProgress(state: DraftState, now: number): { step: number; done: boolean } {
    const steps = jobSteps(state).length;
    if (!state.job) return { step: steps - 1, done: true };
    const step = Math.floor((now - state.job.startedAt) / state.job.stepMs);
    return { step: Math.min(Math.max(step, 0), steps - 1), done: step >= steps };
}

const JOB_ERROR = "We could not read enough from this site to draft the 50. Check the Business step and try again.";

/** The draft as the API returns it. */
export function toScanDraft(state: DraftState, now: number, gsc: SearchConnection, extras: { pitchRunning?: boolean } = {}): ScanDraft {
    const building = state.status === "building";
    const failed = state.status === "failed";
    const a = building || failed ? null : assemble(state);
    const rows = a?.rows ?? [];
    const searches = gsc.connected ? a?.sample?.searches ?? [] : [];

    rows.forEach((row) => {
        row.question.alt_count = a ? rowAlternatives(state, a, row).length : 0;
        // Brand rows have no words: Search Console counts match on the row's text.
        const byText = Boolean(row.plan.brand);
        row.question.search = !gsc.connected ? null : byText ? rowSearchHitByText(row.question.text, searches) : rowSearchHit(row.question.fill_values, searches);
        if (row.question.search) row.question.search_basis = byText ? "text" : "fill";
        if (state.baseline) row.question.changed = row.question.text !== (state.baseline[row.plan.key] ?? row.question.text);
        // first_check: a row differs from the pitched 50 (a row the pitch never asked counts).
        const pitched = state.firstCheck?.pitchTexts;
        if (pitched) row.question.changed = row.question.text !== pitched[row.plan.key];
    });
    const changed = rows.filter((r) => r.question.changed).map((r) => r.question.n);
    const firstCheck = state.purpose === "first_check" ? state.firstCheck ?? null : null;
    const vsPitch = firstCheck && a ? changedVsPitch(firstCheck, rows) : null;
    const recipeRows = state.kind === "recipe_change" ? rows : [];
    const recipeChanged = recipeRows.filter((r) => (state.baseline?.[r.plan.key] ?? r.proposed) !== r.proposed).map((r) => r.question.n);

    const questions = rows.map((r) => r.question);
    const brand = isBrand(state);
    const demand = brand && a?.sample?.brand ? brandDemand(a.sample.brand, state.built, questions, state.demandRound ?? 0, state.demandPulledAt ?? state.createdAt) : undefined;

    const decisions = a
        ? computeDecisions({
              kind: state.kind,
              built: state.built,
              business: state.business,
              rows,
              issues: a.issues,
              src: a.src,
              sample: a.sample,
              answered: state.answered,
              alternatives: (row) => rowAlternatives(state, a, row),
              searchConnected: gsc.connected,
              recipeMode: state.recipeMode,
              changed,
              recipeChanged,
              baselineAt: state.kind !== "new_scan" ? state.baselineAt ?? null : null,
              autoReworded: autoRewordedRows(state, rows),
              demand,
              ...(firstCheck
                  ? { firstCheck: { facts: firstCheck, forPlan: state.forPlan, changedVsPitch: vsPitch, nameTokens: a.lintCtx.nameTokens, pitchRunning: extras.pitchRunning ?? false } }
                  : {}),
          })
        : [];

    const recipeChange: ScanDraftRecipeChange | null =
        state.kind === "recipe_change"
            ? {
                  from: state.recipeFrom.stamp,
                  to: RECIPES[state.built.buy].stamp,
                  fromLabel: state.recipeFrom.label,
                  toLabel: RECIPES[state.built.buy].label,
                  mode: state.recipeMode,
                  rows: recipeRows.map((r) => {
                      const current = state.baseline?.[r.plan.key] ?? r.proposed;
                      const same = current === r.proposed;
                      return {
                          n: r.question.n,
                          current,
                          proposed: r.proposed,
                          same,
                          forced: !same && r.plan.key === FORCED_SLOT_KEY,
                          pick: same || state.recipeMode !== "choose_each" ? null : state.oldKeys.includes(r.plan.key) ? "keep" : "take",
                          // The old wording's result in the latest scan; a row left out was not measured.
                          last: a?.sample?.existing?.lastResults?.[r.plan.key] ?? null,
                      };
                  }),
              }
            : null;

    const search: ScanDraftSearchState = gsc.connected
        ? {
              connected: true,
              status: "connected",
              property: gsc.property,
              fetchedAt: iso(gsc.connectedAt),
              matched: rows.filter((r) => r.question.search).length,
              absentReason: null,
              pulling: gsc.pulling,
          }
        : { connected: false, status: "not_connected", property: null, fetchedAt: null, matched: 0, absentReason: null, pulling: false };

    const progress = jobProgress(state, now);
    const existing = state.kind !== "new_scan";
    const lastUndo = state.undo[state.undo.length - 1];

    return {
        id: state.id,
        kind: state.kind,
        ...(state.purpose ? { purpose: state.purpose } : {}),
        ...(firstCheck
            ? {
                  carriedApproval: state.carriedApproval ?? null,
                  changedVsPitch: vsPitch,
                  firstCheck: {
                      pitch: firstCheck.pitch,
                      quote: firstCheck.quote,
                      contactName: firstCheck.contactName || null,
                      answeredAt: firstCheck.answeredAt,
                      planOutlets: [...firstCheck.planOutlets],
                      heldKeywords: [...firstCheck.heldKeywords],
                      forPlan: {
                          priorityKeywords: state.forPlan?.priorityKeywords ?? null,
                          outletNames: state.forPlan?.outletNames ?? null,
                      },
                      form: { goals: [...(firstCheck.goals ?? [])], plans: firstCheck.plans ?? "", other: firstCheck.other ?? "" },
                      customerQuestions:
                          a && state.built.buy !== "online"
                              ? customerQuestionOffers({ facts: firstCheck, business: state.business, rows, src: a.src, issues: a.issues })
                              : [],
                  },
              }
            : {}),
        status: state.status,
        version: state.version,
        domain: state.domain,
        basePath: "",
        businessId: state.businessId,
        sourceAssessmentId: state.sourceAssessmentId,
        recipe: recipeOf(state),
        business: JSON.parse(JSON.stringify(state.business)) as ScanDraft["business"],
        job: state.job
            ? {
                  id: `job-${state.id}-${state.job.seq}`,
                  status: failed ? "failed" : building ? "running" : "done",
                  step: jobSteps(state)[progress.step],
                  startedAt: iso(state.job.startedAt),
                  error: failed ? brandUnavailable(state) ?? JOB_ERROR : null,
                  ...(state.job.unlocked?.length && !building && !failed ? { unlocked: [...state.job.unlocked] } : {}),
              }
            : null,
        questions,
        words: a ? buildWords(rows, a.src) : [],
        counts: countsOf(questions, brand),
        lint: {
            errors: a ? a.issues.filter((i) => i.severity === "error").map((i) => i.message) : [],
            warnings: a ? a.issues.filter((i) => i.severity === "warning").map((i) => i.message) : [],
            checkedAt: a ? iso(state.updatedAt) : null,
        },
        decisions,
        recipeChange,
        // first_check against the pitch: the rows "See the changes" opens. No trend break (no trend yet).
        diff: existing || firstCheck?.pitchTexts ? { changed, total: questions.length } : null,
        trendBreak: existing
            ? {
                  willBreak: changed.length > 0,
                  reason: changed.length ? `${changed.length} of ${questions.length} questions change wording, so their trend restarts.` : null,
                  slots: changed,
              }
            : null,
        search,
        ...(demand ? { demand } : {}),
        summary: {
            estimatedCostUsd: 1.7,
            estimatedMinutes: 40, // FIRST_CHECK_MINUTES (onboarding/derive), measured
            probeMode: "web_search",
            scopeWarning: null,
            note: state.note,
            requestedBy: state.business.requestedBy,
        },
        // The business's baseline: Start offers to move it only in its first 30 days (call 12).
        baseline:
            state.kind !== "new_scan" && state.baselineAt
                ? { at: new Date(state.baselineAt).toISOString(), movableUntil: new Date(state.baselineAt + BASELINE_MOVE_DAYS * 86_400_000).toISOString() }
                : null,
        monthly: state.kind !== "new_scan" && state.monthlyAt ? { nextAt: new Date(state.monthlyAt).toISOString() } : null,
        scheduledFor: state.scheduledFor ? new Date(state.scheduledFor).toISOString() : null,
        stale: !building && isStale(state),
        undo: { available: state.undo.length, lastLabel: lastUndo ? lastUndo.label : null },
        decisionsAnswered: state.answeredCount ?? 0,
        createdAt: new Date(state.createdAt).toISOString(),
        updatedAt: new Date(state.updatedAt).toISOString(),
        approvedAt: iso(state.approvedAt),
        startedAssessmentId: state.startedAssessmentId,
    };
}
